import { useMemo, useState } from 'react'
import { router } from '@inertiajs/react'
import { Search, Ship, Loader2, ArrowRight, RefreshCw, AlertTriangle } from 'lucide-react'
import { Link } from '@inertiajs/react'
import { formatDate } from '@/lib/formatters'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { toast } from 'sonner'
import AuthenticatedLayout from '@/layouts/AuthenticatedLayout'
import PortCombobox from '@/components/ports/PortCombobox'
import { useOrgPath } from '@/lib/pathFor'
import { rateSourceLabel, rateSourceBadgeClass } from '@/lib/rateSource'

interface Client { id: string; name: string }
interface ContainerTypeOption { iso: string; shorthand: string; label: string }
interface Carrier { code: string; name: string }

interface Offer {
  id: string
  rate_source?: string | null
  carrier_code: string | null
  carrier_name: string | null
  total_per_container_usd: number | null
  total_all_containers_usd: number | null
  transit_days: number | null
  departure_date: string | null
  arrival_date: string | null
  offer_valid_until: string | null
  rate_validity: string | null
  container_type: string | null
  container_count: number | null
}

export interface Quote {
  id: string
  internal_reference: string
  shipment_internal_reference: string | null
  show_url: string | null
  client_name: string | null
  origin_port: string | null
  destination_port: string | null
  carrier_name: string | null
  container_type: string | null
  container_count: number | null
  sell_total_usd: number
  valid_until: string | null
  rate_validity: string | null
  expired: boolean
  salesperson_name: string | null
  created_at: string
}

interface RateSearchIndexProps {
  clients: Client[]
  container_types: ContainerTypeOption[]
  carriers: Carrier[]
  quotes: Quote[]
  quotes_search: string
  live_carrier_names?: string[]
}

type Tab = 'search' | 'quotes'

// Sales follow-up list: every booking still in "quoted", newest first.
export function QuotesList({ quotes, search, pathFor }: { quotes: Quote[]; search: string; pathFor: (p: string) => string }) {
  const [q, setQ] = useState(search)
  function submit(e: React.FormEvent) {
    e.preventDefault()
    router.get(pathFor('/rate-search'), { q, tab: 'quotes' }, { preserveState: true })
  }
  return (
    <div>
      <form onSubmit={submit} className="flex items-center gap-2 border-b bg-muted/30 px-8 py-3">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Client, ref, route…" className="h-8 w-[280px] pl-8 text-[13px]" />
        </div>
        <Button type="submit" size="sm" variant="outline" className="cursor-pointer">Search quotes</Button>
        <span className="ml-auto text-xs text-muted-foreground">{quotes.length} open quote{quotes.length === 1 ? '' : 's'}</span>
      </form>
      <table className="w-full">
        <thead>
          <tr className="border-b bg-muted/30 text-left">
            {['Quote', 'Client', 'Route', 'Carrier', 'Containers', 'Sell total', 'Valid until', 'Salesperson', 'Created'].map((h) => (
              <th key={h} className="px-4 py-2.5 text-xs font-medium text-muted-foreground">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {quotes.map((quote) => (
            <tr key={quote.id} className="border-b hover:bg-muted/30 cursor-pointer" onClick={() => quote.show_url && router.visit(quote.show_url)}>
              <td className="px-4 py-3">
                {quote.show_url ? (
                  <Link href={quote.show_url} className="font-mono text-[13px] font-semibold hover:underline">{quote.internal_reference}</Link>
                ) : (
                  <span className="font-mono text-[13px] font-semibold">{quote.internal_reference}</span>
                )}
                <div className="font-mono text-[10px] text-muted-foreground">{quote.shipment_internal_reference}</div>
              </td>
              <td className="px-4 py-3 text-[13px]">{quote.client_name || '—'}</td>
              <td className="px-4 py-3 text-[13px] text-muted-foreground">{quote.origin_port || '?'} → {quote.destination_port || '?'}</td>
              <td className="px-4 py-3 text-[13px]">{quote.carrier_name || '—'}</td>
              <td className="px-4 py-3 text-[13px] text-muted-foreground">{quote.container_count ?? 1}× {quote.container_type || '—'}</td>
              <td className="px-4 py-3 text-[13px] font-semibold">{usd(quote.sell_total_usd)}</td>
              <td className="px-4 py-3 text-[13px]">
                {quote.rate_validity || (quote.valid_until ? formatDate(quote.valid_until) : '—')}
                {quote.expired && <span className="ml-1.5 rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-medium text-red-800" title="The carrier offer behind this quote has lapsed; re-check rates before booking">Offer lapsed</span>}
              </td>
              <td className="px-4 py-3 text-[13px] text-muted-foreground">{quote.salesperson_name || '—'}</td>
              <td className="px-4 py-3 text-[13px] text-muted-foreground">{formatDate(quote.created_at)}</td>
            </tr>
          ))}
          {quotes.length === 0 && (
            <tr><td colSpan={9} className="px-6 py-8 text-center text-sm text-muted-foreground">No open quotes{search ? ' match this search' : ''}.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

type RateStatus = 'quoted' | 'no_offers' | 'api_error' | 'no_credentials' | 'invalid_rate_input' | null
type RouteError = { carrier: string; error: string }
type Unparseable = { carrier: string; carrier_name: string; count: number; reason: string | null }
const UNPARSEABLE_REASONS: Record<string, string> = {
  graduated_pricing: 'tiered pricing we cannot total',
  no_usd_sea_freight: 'no USD ocean freight line',
}

// Empty result states differ operationally: a carrier timeout is not "no coverage".
export function RateSearchEmptyState({ status, errors, onRetry }: { status: RateStatus; errors: RouteError[]; onRetry: () => void }) {
  if (status === 'api_error') {
    return (
      <div className="rounded-md border border-dashed border-amber-300 bg-amber-50 p-8 text-center">
        <p className="flex items-center justify-center gap-1.5 text-sm font-medium text-amber-900"><AlertTriangle className="h-4 w-4" /> Carrier lookup failed — this is not a coverage result.</p>
        <ul className="mt-2 space-y-0.5 text-xs text-amber-800">
          {errors.map((e, i) => (<li key={i}>{e.error}</li>))}
        </ul>
        <Button type="button" size="sm" variant="outline" className="mt-4 cursor-pointer" onClick={onRetry}>
          <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Retry
        </Button>
      </div>
    )
  }
  if (status === 'no_credentials') {
    return (
      <div className="rounded-md border border-dashed p-8 text-center">
        <p className="text-sm font-medium">No carrier API connected.</p>
        <p className="mt-1 text-xs text-muted-foreground">Add carrier API credentials under Settings → Carriers, or upload a rate sheet.</p>
      </div>
    )
  }
  return (
    <div className="rounded-md border border-dashed p-8 text-center">
      <p className="text-sm font-medium">No rates found for this route.</p>
      <p className="mt-1 text-xs text-muted-foreground">The carrier answered but has no offer for this lane, container and date.</p>
    </div>
  )
}

const selectClasses =
  'mt-1 flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-[13px] shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring cursor-pointer'

function defaultDepartureDate(): string {
  const d = new Date()
  d.setDate(d.getDate() + 21) // sane cargo-ready lead, matching the quote default
  return d.toISOString().slice(0, 10)
}

function usd(value: number | null | undefined): string {
  if (value == null) return '—'
  return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

export default function RateSearchIndex({ clients, container_types, carriers, quotes = [], quotes_search = '', live_carrier_names = [] }: RateSearchIndexProps) {
  const pathFor = useOrgPath()
  const [tab, setTab] = useState<Tab>(() =>
    quotes_search || (typeof window !== 'undefined' && window.location.search.includes('tab=quotes')) ? 'quotes' : 'search',
  )

  const [form, setForm] = useState({
    origin_locode: '',
    destination_locode: '',
    container_type: '40HC',
    container_count: '1',
    departure_date: defaultDepartureDate(),
    carrier_code: '',
  })
  const [routeValid, setRouteValid] = useState({ origin: true, destination: true })
  const [loading, setLoading] = useState(false)
  const [searched, setSearched] = useState(false)
  const [offers, setOffers] = useState<Offer[]>([])
  const [routeErrors, setRouteErrors] = useState<RouteError[]>([])
  const [rateStatus, setRateStatus] = useState<RateStatus>(null)
  const [unparseable, setUnparseable] = useState<Unparseable[]>([])
  const [cachedAt, setCachedAt] = useState<string | null>(null)
  const [selectedOffer, setSelectedOffer] = useState<Offer | null>(null)
  // Only carriers with a live API credential are actually asked; contract sheets are local.
  const carrierNames = form.carrier_code
    ? carriers.filter((c) => c.code === form.carrier_code).map((c) => c.name).join(', ')
    : live_carrier_names.join(', ')

  const canSearch = useMemo(
    () => form.origin_locode && form.destination_locode && routeValid.origin && routeValid.destination,
    [form.origin_locode, form.destination_locode, routeValid],
  )

  async function handleSearch(e: React.FormEvent | null, refresh = false) {
    e?.preventDefault()
    if (!canSearch) {
      toast.error('Select a valid origin and destination port.')
      return
    }
    setLoading(true)
    setSearched(true)
    setOffers([])
    setRouteErrors([])
    setRateStatus(null)
    setCachedAt(null)
    setUnparseable([])
    try {
      const csrf = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)?.content || ''
      const res = await fetch(pathFor('/rate-search/quote'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': csrf },
        body: JSON.stringify({ ...form, refresh }),
      })
      const body = await res.json()
      setOffers((body.offers || []) as Offer[])
      setRouteErrors((body.errors || []) as RouteError[])
      setRateStatus((body.rate_status || null) as RateStatus)
      setUnparseable((body.unparseable || []) as Unparseable[])
      setCachedAt(body.cached_at || null)
    } catch {
      toast.error('Could not fetch rates. Try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthenticatedLayout>
      <header className="flex h-14 items-center border-b px-8">
        <h1 className="text-sm font-semibold">Rate Search</h1>
        <span className="ml-3 text-xs text-muted-foreground">Search live carrier rates, then quote a customer from one.</span>
        <div className="ml-auto flex gap-1 text-[12px]" role="tablist">
          {([['search', 'Search rates'], ['quotes', `Quotes (${quotes.length})`]] as [Tab, string][]).map(([key, label]) => (
            <button key={key} type="button" role="tab" aria-selected={tab === key}
              className={`rounded-md px-2.5 py-1 cursor-pointer ${tab === key ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
              onClick={() => setTab(key)}>{label}</button>
          ))}
        </div>
      </header>

      {tab === 'quotes' && (
        <div className="flex-1 overflow-y-auto">
          <QuotesList quotes={quotes} search={quotes_search} pathFor={pathFor} />
        </div>
      )}

      <div className={tab === 'search' ? 'flex-1 overflow-y-auto' : 'hidden'}>
        {/* Search form */}
        <form onSubmit={handleSearch} className="border-b bg-muted/30 px-8 py-5">
          <div className="grid grid-cols-2 gap-4 md:grid-cols-6">
            <div className="col-span-1 md:col-span-2">
              <Label className="text-xs">Origin</Label>
              <PortCombobox
                placeholder="e.g. Shanghai or CNSHA"
                value={form.origin_locode}
                onChange={(v) => setForm((f) => ({ ...f, origin_locode: v }))}
                onValidityChange={(valid) => setRouteValid((s) => ({ ...s, origin: valid }))}
              />
            </div>
            <div className="col-span-1 md:col-span-2">
              <Label className="text-xs">Destination</Label>
              <PortCombobox
                placeholder="e.g. Los Angeles or USLAX"
                value={form.destination_locode}
                onChange={(v) => setForm((f) => ({ ...f, destination_locode: v }))}
                onValidityChange={(valid) => setRouteValid((s) => ({ ...s, destination: valid }))}
              />
            </div>
            <div>
              <Label htmlFor="container_type" className="text-xs">Container</Label>
              <select id="container_type" className={selectClasses} value={form.container_type}
                onChange={(e) => setForm((f) => ({ ...f, container_type: e.target.value }))}>
                {container_types.map((t) => (
                  <option key={t.iso} value={t.shorthand}>{t.shorthand}</option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="container_count" className="text-xs">Qty</Label>
              <Input id="container_count" type="number" min={1} className="mt-1 h-9 text-[13px]"
                value={form.container_count}
                onChange={(e) => setForm((f) => ({ ...f, container_count: e.target.value }))} />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="departure_date" className="text-xs">Cargo-ready date</Label>
              <Input id="departure_date" type="date" className="mt-1 h-9 text-[13px]"
                value={form.departure_date}
                onChange={(e) => setForm((f) => ({ ...f, departure_date: e.target.value }))} />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="carrier_code" className="text-xs">Carrier (optional)</Label>
              <select id="carrier_code" className={selectClasses} value={form.carrier_code}
                onChange={(e) => setForm((f) => ({ ...f, carrier_code: e.target.value }))}>
                <option value="">All carriers</option>
                {carriers.map((c) => (<option key={c.code} value={c.code}>{c.name}</option>))}
              </select>
            </div>
            <div className="flex items-end">
              <Button type="submit" size="sm" className="cursor-pointer" disabled={loading || !canSearch}>
                {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}
                <span className="ml-1.5">Search rates</span>
              </Button>
            </div>
          </div>
        </form>

        {/* Results */}
        <div className="px-8 py-6">
          {loading && (
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Asking {carrierNames || 'carriers'} for live rates… long lanes can take up to 30 seconds.
            </p>
          )}

          {!loading && searched && offers.length === 0 && (
            <RateSearchEmptyState status={rateStatus} errors={routeErrors} onRetry={() => handleSearch(null, true)} />
          )}

          {!loading && offers.length > 0 && (
            <div className="space-y-2">
              {routeErrors.length > 0 && (
                <div className="mb-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                  <span className="font-medium">Some carriers did not answer:</span>{' '}
                  {routeErrors.map((e) => e.error).join(' · ')}{' '}
                  <button type="button" className="underline cursor-pointer" onClick={() => handleSearch(null, true)}>Retry</button>
                </div>
              )}
              <p className="mb-3 flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
                <span>{offers.length} rate{offers.length === 1 ? '' : 's'} — carrier cost shown; your markup is applied when you create the quote.</span>
                {unparseable.map((u) => (
                  <span key={u.carrier}>· {u.count} {u.carrier_name} offer{u.count === 1 ? '' : 's'} hidden ({UNPARSEABLE_REASONS[u.reason || ''] || 'unsupported pricing'})</span>
                ))}
                {cachedAt && (
                  <span>
                    · Retrieved {new Date(cachedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                    {' '}<button type="button" className="underline cursor-pointer" onClick={() => handleSearch(null, true)}>Refresh from carrier</button>
                  </span>
                )}
              </p>
              {offers.map((offer) => (
                <div key={offer.id} className="flex items-center justify-between rounded-lg border bg-card px-4 py-3">
                  <div className="grid flex-1 grid-cols-2 gap-x-6 gap-y-1 md:grid-cols-5">
                    <div>
                      <div className="text-[11px] uppercase text-muted-foreground">Carrier</div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm font-medium">{offer.carrier_name || offer.carrier_code || '—'}</span>
                        {offer.rate_source === 'rate_sheet' && (
                          <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${rateSourceBadgeClass('rate_sheet')}`}>{rateSourceLabel('rate_sheet')}</span>
                        )}
                      </div>
                    </div>
                    <div>
                      <div className="text-[11px] uppercase text-muted-foreground">Per container</div>
                      <div className="text-sm font-semibold">{usd(offer.total_per_container_usd)}</div>
                    </div>
                    <div>
                      <div className="text-[11px] uppercase text-muted-foreground">Total</div>
                      <div className="text-sm">{usd(offer.total_all_containers_usd)}</div>
                    </div>
                    <div>
                      <div className="text-[11px] uppercase text-muted-foreground">Transit / departs</div>
                      <div className="text-sm">{offer.transit_days != null ? `${offer.transit_days}d` : '—'}{offer.departure_date ? ` · ${String(offer.departure_date).slice(0, 10)}` : ''}</div>
                    </div>
                    <div>
                      <div className="text-[11px] uppercase text-muted-foreground">Rate validity</div>
                      <div className="text-sm">{offer.rate_validity || '—'}</div>
                      {offer.offer_valid_until && (
                        <div className="text-[11px] text-muted-foreground" title="Book (or re-check) before the carrier withdraws this offer">
                          Offer open until {String(offer.offer_valid_until).slice(0, 16).replace('T', ' ')}
                        </div>
                      )}
                    </div>
                  </div>
                  <Button size="sm" className="ml-4 cursor-pointer" onClick={() => setSelectedOffer(offer)}>
                    Quote this rate <ArrowRight className="ml-1 h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          {!searched && (
            <div className="flex flex-col items-center justify-center py-16 text-center text-muted-foreground">
              <Ship className="mb-3 h-8 w-8 opacity-40" />
              <p className="text-sm">Enter a route and search to see live carrier rates.</p>
            </div>
          )}
        </div>
      </div>

      {selectedOffer && (
        <CreateFromRateModal
          offer={selectedOffer}
          route={{ origin_locode: form.origin_locode, destination_locode: form.destination_locode }}
          clients={clients}
          pathFor={pathFor}
          onClose={() => setSelectedOffer(null)}
        />
      )}
    </AuthenticatedLayout>
  )
}

function CreateFromRateModal({
  offer, route, clients, pathFor, onClose,
}: {
  offer: Offer
  route: { origin_locode: string; destination_locode: string }
  clients: Client[]
  pathFor: (p: string) => string
  onClose: () => void
}) {
  const [mode, setMode] = useState<'existing' | 'new'>(clients.length > 0 ? 'existing' : 'new')
  const [clientId, setClientId] = useState('')
  const [newName, setNewName] = useState('')
  const [newEmail, setNewEmail] = useState('')
  const [commodity, setCommodity] = useState('')
  const [processing, setProcessing] = useState(false)

  const customerReady = mode === 'existing' ? Boolean(clientId) : newName.trim().length > 0

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!customerReady) {
      toast.error('Choose or name a customer.')
      return
    }
    setProcessing(true)
    router.post(pathFor('/rate-search/shipment'), {
      carrier_offer_id: offer.id,
      origin_port: route.origin_locode,
      destination_port: route.destination_locode,
      commodity,
      container_count: offer.container_count ?? undefined,
      client_id: mode === 'existing' ? clientId : undefined,
      new_client_name: mode === 'new' ? newName : undefined,
      new_client_email: mode === 'new' ? newEmail : undefined,
    }, {
      onError: () => { toast.error('Could not create the quote.'); setProcessing(false) },
      onFinish: () => setProcessing(false),
    })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div className="rounded-xl bg-white shadow-lg" style={{ width: 460 }}>
        <div className="px-5 pt-5 pb-3">
          <h2 className="text-base font-semibold">Create quote</h2>
          <p className="mt-1 text-xs text-muted-foreground">
            {route.origin_locode} → {route.destination_locode} · {offer.carrier_name || offer.carrier_code} · {usd(offer.total_per_container_usd)}/container
          </p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4 px-5 pb-5">
          <div>
            <div className="mb-1 flex items-center gap-3">
              <Label className="text-xs">Customer</Label>
              <div className="flex gap-1 text-[11px]">
                <button type="button" className={`rounded px-2 py-0.5 ${mode === 'existing' ? 'bg-primary text-primary-foreground' : 'bg-muted'}`} onClick={() => setMode('existing')}>Existing</button>
                <button type="button" className={`rounded px-2 py-0.5 ${mode === 'new' ? 'bg-primary text-primary-foreground' : 'bg-muted'}`} onClick={() => setMode('new')}>New</button>
              </div>
            </div>
            {mode === 'existing' ? (
              <select className={selectClasses} value={clientId} onChange={(e) => setClientId(e.target.value)}>
                <option value="">Select a customer…</option>
                {clients.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
              </select>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <Input placeholder="Customer name" className="h-9 text-[13px]" value={newName} onChange={(e) => setNewName(e.target.value)} />
                <Input placeholder="Email (optional)" type="email" className="h-9 text-[13px]" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} />
              </div>
            )}
          </div>
          <div>
            <Label htmlFor="rate_commodity" className="text-xs">Commodity (optional)</Label>
            <Input id="rate_commodity" placeholder="e.g. Electronics" className="mt-1 h-9 text-[13px]" value={commodity} onChange={(e) => setCommodity(e.target.value)} />
          </div>
          <p className="text-[11px] text-muted-foreground">Creates a shipment record with this rate as a quoted booking. It stays under Quotes until the customer accepts.</p>
          <div className="flex items-center justify-end gap-2 pt-1">
            <Button type="button" variant="outline" size="sm" className="cursor-pointer" onClick={onClose}>Cancel</Button>
            <Button type="submit" size="sm" className="cursor-pointer" disabled={processing || !customerReady}>
              {processing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
              <span className={processing ? 'ml-1.5' : ''}>Create quote</span>
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
